import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Leaf, ArrowRight, CheckCircle } from 'lucide-react';
import { PartnerLogos } from '../components/PartnerLogos';

const BRAND_LOGO_DARK = "/benchmark-ph-dark.png";

export default function LandingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteCode = searchParams.get('invite');

  const handleGetStarted = () => {
    if (inviteCode) {
      navigate(`/auth?invite=${inviteCode}`);
    } else {
      navigate('/auth');
    }
  };

  const features = [
    {
      icon: "/icons/icon-cloud-network-navy.png",
      title: "Double Materiality Assessment",
      description: "Evaluate both financial impact and social/environmental significance of ESG issues"
    },
    {
      icon: "/icons/icon-laptop-share-navy.png",
      title: "Industry Benchmarking",
      description: "Compare your organization's ESG performance against IT-BPM industry standards"
    },
    {
      icon: "/icons/icon-laptop-navy.png",
      title: "AI-Powered Assistance",
      description: "Get expert guidance from our ESG AI Advisor while completing the questionnaire"
    },
    {
      icon: "/icons/icon-globe-tech-navy.png",
      title: "Stakeholder Insights",
      description: "Contribute to industry-wide sustainability data consolidation"
    }
  ];

  const sections = [
    { letter: "A", title: "Organisational Profile", items: "Employee count, business segment, operational sites" },
    { letter: "B", title: "Double Materiality Assessment", items: "13 ESG issues rated on financial & impact relevance" },
    { letter: "C", title: "Quantitative KPI Inputs", items: "Data privacy, talent, climate, energy, emissions & more" },
    { letter: "D", title: "Forward-Looking Insights", items: "ESG targets and future risk assessment" }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero Section */}
      <section className="hero-gradient min-h-screen relative overflow-hidden">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-accent rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-teal-500 rounded-full blur-3xl"></div>
        </div>

        <div className="relative z-10">
          {/* Header */}
          <header className="px-6 md:px-12 lg:px-24 py-6">
            <nav className="flex items-center justify-between">
              <img 
                src={BRAND_LOGO_DARK} 
                alt="Benchmark PH — IT-BPM ESG Pulse Navigator" 
                className="h-12 md:h-16 object-contain"
                data-testid="ibpap-logo"
              />
              <div className="flex items-center gap-5">
                <button
                  onClick={() => navigate('/state-of-esg')}
                  className="text-accent hover:text-accent/80 text-sm font-medium transition-colors"
                  data-testid="insights-link"
                >
                  Industry Insights
                </button>
              </div>
            </nav>
          </header>

          {/* Hero Content */}
          <div className="px-6 md:px-12 lg:px-24 py-12 md:py-24">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
              {/* Left Column */}
              <div className="lg:col-span-7 animate-fade-in">
                <div className="inline-flex items-center gap-2 bg-accent/20 text-accent px-4 py-2 rounded-full text-sm font-medium mb-6">
                  <Leaf className="w-4 h-4" />
                  <span>The First Industry-Specific ESG Benchmarking Intelligence Engine</span>
                </div>
                
                <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white leading-tight mb-6">
                  ESG Double Materiality
                  <span className="block text-accent">Industry Survey</span>
                </h1>
                
                <p className="text-lg md:text-xl text-slate-300 mb-8 max-w-2xl leading-relaxed">
                  Participate in the annual sustainability data consolidation for the Philippine 
                  IT-BPM industry. Your insights help shape industry-wide ESG standards and benchmarks.
                </p>

                <div className="flex flex-col sm:flex-row gap-4">
                  <button
                    onClick={handleGetStarted}
                    className="btn-accent flex items-center justify-center gap-2 text-lg"
                    data-testid="get-started-btn"
                  >
                    Start Questionnaire
                    <ArrowRight className="w-5 h-5" />
                  </button>
                  <a
                    href="#about"
                    className="btn-secondary bg-white/10 text-white border-white/20 hover:bg-white/20 flex items-center justify-center"
                    data-testid="learn-more-btn"
                  >
                    Learn More
                  </a>
                </div>

                {inviteCode && (
                  <div className="mt-6 inline-flex items-center gap-2 bg-accent/20 text-accent px-4 py-2 rounded-lg text-sm">
                    <CheckCircle className="w-4 h-4" />
                    <span>You have an invitation code applied</span>
                  </div>
                )}
              </div>

              {/* Right Column - Stats */}
              <div className="lg:col-span-5 animate-fade-in" style={{ animationDelay: '0.2s' }}>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
                    <img src="/icons/icon-community-white.png" alt="" className="w-8 h-8 object-contain mb-3 opacity-90" />
                    <div className="text-4xl font-bold text-white mb-2">13</div>
                    <div className="text-slate-400 text-sm">Material ESG Issues</div>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
                    <img src="/icons/icon-presentation-white.png" alt="" className="w-8 h-8 object-contain mb-3 opacity-90" />
                    <div className="text-4xl font-bold text-accent mb-2">4</div>
                    <div className="text-slate-400 text-sm">Survey Sections</div>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
                    <img src="/icons/icon-gear-person-white.png" alt="" className="w-8 h-8 object-contain mb-3 opacity-90" />
                    <div className="text-4xl font-bold text-accent mb-2">AI</div>
                    <div className="text-slate-400 text-sm">Powered Advisor</div>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
                    <img src="/icons/icon-collaboration-white.png" alt="" className="w-8 h-8 object-contain mb-3 opacity-90" />
                    <div className="text-4xl font-bold text-white mb-2">100%</div>
                    <div className="text-slate-400 text-sm">Confidential</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="about" className="px-6 md:px-12 lg:px-24 py-20 bg-white">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-5xl font-bold text-primary mb-4">
            Why Participate?
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Help build a comprehensive ESG picture of the Philippine IT-BPM industry
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((feature, index) => (
            <div 
              key={index}
              className="section-card card-hover"
              data-testid={`feature-card-${index}`}
            >
              <div className="w-14 h-14 bg-accent/10 rounded-xl flex items-center justify-center mb-4 p-2.5">
                <img src={feature.icon} alt="" className="w-full h-full object-contain" />
              </div>
              <h3 className="text-lg font-semibold text-primary mb-2">{feature.title}</h3>
              <p className="text-slate-600 text-sm">{feature.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Survey Structure Section */}
      <section className="px-6 md:px-12 lg:px-24 py-20 bg-slate-50">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-5xl font-bold text-primary mb-4">
            Survey Structure
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Four comprehensive sections covering all aspects of ESG performance
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          {sections.map((section, index) => (
            <div 
              key={index}
              className="bg-white rounded-xl p-6 border border-slate-200 flex gap-4 card-hover"
              data-testid={`section-card-${section.letter}`}
            >
              <div className="flex-shrink-0 w-14 h-14 bg-primary rounded-xl flex items-center justify-center text-accent text-xl font-bold">
                {section.letter}
              </div>
              <div>
                <h3 className="text-lg font-semibold text-primary mb-1">{section.title}</h3>
                <p className="text-slate-500 text-sm">{section.items}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="px-6 md:px-12 lg:px-24 py-20 bg-gradient-to-br from-primary via-brand-navylight to-primary">
        <div className="text-center max-w-3xl mx-auto">
          <img src="/icons/icon-community-white.png" alt="" className="w-16 h-16 object-contain mx-auto mb-6" />
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Make an Impact?
          </h2>
          <p className="text-lg text-slate-300 mb-8">
            Join leading IT-BPM companies in the Philippines in driving sustainable business practices.
          </p>
          <button
            onClick={handleGetStarted}
            className="btn-accent flex items-center justify-center gap-2 mx-auto text-lg"
            data-testid="cta-start-btn"
          >
            Begin Your Assessment
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </section>

      {/* Footer */}
      {/* Footer */}
      <footer className="px-6 md:px-12 lg:px-24 py-12 bg-primary">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <img 
            src={BRAND_LOGO_DARK} 
            alt="Benchmark PH" 
            className="h-12 object-contain opacity-90"
          />
          <PartnerLogos variant="dark" />
          <div className="text-center md:text-right">
            <p className="text-slate-400 text-sm">
              © {new Date().getFullYear()} Benchmark PH — IT-BPM ESG Pulse Navigator
            </p>
            <p className="text-slate-500 text-sm mt-1" data-testid="footer-credit">
              Developed for <span className="text-slate-300">IBPAP</span> by{' '}
              <a 
                href="https://sustek.ai/solutions/soctech" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-accent hover:text-accent/80 hover:underline"
              >
                SusTek SocTech Labs
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
